import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Card, EmptyState, Field, Icon, Input, Select, Spinner } from '../components/ui';
import { ApiError, api } from '../lib/api';
import { brl, datePt, todayIso } from '../lib/format';
import { usePageMeta } from '../lib/seo';
import { tr } from '../lib/i18n';

interface SearchItem {
  professionalId: string;
  professionalName: string;
  professionalSlug: string;
  title: string | null;
  photoPath: string | null;
  businessName: string;
  businessSlug: string;
  categoryKey: string;
  categoryLabel: string | null;
  city: string | null;
  district: string | null;
  minPriceCents: number | null;
  serviceCount: number;
}

interface SearchResponse {
  results: SearchItem[];
  total: number;
  limit: number;
  offset: number;
  dateFilter: string | null;
}

const PAGE_SIZE = 24;

interface Filters {
  q: string;
  category: string;
  city: string;
  district: string;
  minPrice: string;
  maxPrice: string;
  date: string;
}

function emptyFilters(): Filters {
  return { q: '', category: '', city: '', district: '', minPrice: '', maxPrice: '', date: '' };
}

export function SearchPage(): JSX.Element {
  usePageMeta(
    tr('Encontrar profissional'),
    tr('Busque profissionais e estabelecimentos de beleza, saúde, bem-estar e serviços por nome, categoria, cidade, bairro, preço e disponibilidade.'),
    '/busca'
  );

  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useState<Filters>(() => ({
    q: params.get('q') ?? '',
    category: params.get('category') ?? '',
    city: params.get('city') ?? '',
    district: params.get('district') ?? '',
    minPrice: params.get('minPrice') ?? '',
    maxPrice: params.get('maxPrice') ?? '',
    date: params.get('date') ?? ''
  }));

  const [results, setResults] = useState<SearchItem[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  const [categories, setCategories] = useState<Record<string, string>>({});

  const query = useMemo(() => {
    const sp = new URLSearchParams();
    if (filters.q) sp.set('q', filters.q);
    if (filters.category) sp.set('category', filters.category);
    if (filters.city) sp.set('city', filters.city);
    if (filters.district) sp.set('district', filters.district);
    if (filters.minPrice) sp.set('minPrice', filters.minPrice);
    if (filters.maxPrice) sp.set('maxPrice', filters.maxPrice);
    if (filters.date) sp.set('date', filters.date);
    return sp.toString();
  }, [filters]);

  const runSearch = useCallback(
    async (nextOffset: number, append: boolean, queryOverride?: string) => {
      const effectiveQuery = queryOverride ?? query;
      setLoading(true);
      setError(null);
      try {
        const qs = effectiveQuery ? `${effectiveQuery}&` : '';
        const res = await api.get<SearchResponse>(
          `/api/public/search?${qs}limit=${PAGE_SIZE}&offset=${nextOffset}`
        );
        setResults((prev) => (append ? [...prev, ...res.results] : res.results));
        setTotal(res.total);
        setOffset(nextOffset + res.results.length);
        setSearched(true);
        setCategories((prev) => {
          const next = { ...prev };
          for (const r of res.results) {
            if (r.categoryKey && r.categoryLabel) next[r.categoryKey] = r.categoryLabel;
          }
          return next;
        });
        setParams(new URLSearchParams(qs), { replace: true });
      } catch (err: unknown) {
        setError(err instanceof ApiError ? err.message : tr('Não foi possível buscar no momento.'));
        if (!append) setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [query, setParams]
  );

  useEffect(() => {
    void runSearch(0, false);
    // Run once on mount; subsequent searches are triggered by the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void runSearch(0, false);
  };

  const reset = () => {
    setFilters(emptyFilters());
    void runSearch(0, false, '');
  };

  const hasMore = results.length < total;
  const categoryOptions = Object.entries(categories);

  return (
    <div className="mx-auto w-full max-w-content px-4 py-10">
      <h1 className="font-display text-3xl font-semibold text-ink">{tr("Encontrar profissional")}</h1>
      <p className="mt-2 text-muted">
        {tr("Refine por nome, categoria, localização, faixa de preço e disponibilidade em uma data específica.")}
      </p>

      <Card as="section" className="mt-6">
        <form onSubmit={onSubmit} className="grid gap-4 md:grid-cols-3">
          <Field label={tr("Nome do profissional ou estabelecimento")}>
            {(props) => (
              <Input
                {...props}
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                placeholder={tr("Ex.: Ana, Studio Bella")}
              />
            )}
          </Field>

          <Field label={tr("Categoria")}>
            {(props) => (
              <Select
                {...props}
                value={filters.category}
                onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}
              >
                <option value="">{tr("Todas as categorias")}</option>
                {categoryOptions.map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </Select>
            )}
          </Field>

          <Field label={tr("Disponível na data")} hint="Mostra apenas quem tem horários livres no dia.">
            {(props) => (
              <Input
                {...props}
                type="date"
                min={todayIso()}
                value={filters.date}
                onChange={(e) => setFilters((f) => ({ ...f, date: e.target.value }))}
              />
            )}
          </Field>

          <Field label={tr("Cidade")}>
            {(props) => (
              <Input
                {...props}
                value={filters.city}
                onChange={(e) => setFilters((f) => ({ ...f, city: e.target.value }))}
                placeholder={tr("Ex.: São Paulo")}
              />
            )}
          </Field>

          <Field label={tr("Bairro")}>
            {(props) => (
              <Input
                {...props}
                value={filters.district}
                onChange={(e) => setFilters((f) => ({ ...f, district: e.target.value }))}
                placeholder={tr("Ex.: Pinheiros")}
              />
            )}
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={tr("Preço mínimo (R$)")}>
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={0}
                  inputMode="numeric"
                  value={filters.minPrice}
                  onChange={(e) => setFilters((f) => ({ ...f, minPrice: e.target.value }))}
                  placeholder="0"
                />
              )}
            </Field>
            <Field label={tr("Preço máximo (R$)")}>
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={0}
                  inputMode="numeric"
                  value={filters.maxPrice}
                  onChange={(e) => setFilters((f) => ({ ...f, maxPrice: e.target.value }))}
                  placeholder={tr("Sem limite")}
                />
              )}
            </Field>
          </div>

          <div className="md:col-span-3 flex flex-wrap gap-3">
            <Button type="submit" icon={<Icon name="search" />} loading={loading}>
              {tr("Buscar")}
            </Button>
            <Button type="button" variant="ghost" onClick={reset}>
              {tr("Limpar filtros")}
            </Button>
          </div>
        </form>
      </Card>

      <div aria-live="polite" className="mt-8">
        {error ? (
          <p className="flex items-center gap-2 text-sm text-danger">
            <Icon name="alert" /> {error}
          </p>
        ) : null}

        {loading && results.length === 0 ? (
          <div className="flex items-center gap-3 py-10 text-muted">
            <Spinner label={tr("Buscando profissionais")} />
          </div>
        ) : null}

        {searched && !loading && !error && results.length === 0 ? (
          <EmptyState
            title={tr("Nenhum profissional encontrado")}
            description={tr("Não há resultados públicos com esses filtros no momento. Tente remover a data ou ampliar a faixa de preço.")}
            action={<Button variant="secondary" onClick={reset}>{tr("Limpar filtros")}</Button>}
          />
        ) : null}

        {results.length > 0 ? (
          <>
            <p className="mb-4 text-sm text-muted">
              {filters.date
                ? tr('{count} {result} com horário livre em {date}.', {
                    count: total,
                    result: total === 1 ? tr('resultado') : tr('resultados'),
                    date: datePt(filters.date)
                  })
                : tr('{count} {result}.', { count: total, result: total === 1 ? tr('resultado') : tr('resultados') })}
            </p>
            <ul className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {results.map((r) => (
                <li key={`${r.professionalId}`}>
                  <ResultCard item={r} />
                </li>
              ))}
            </ul>
            {hasMore ? (
              <div className="mt-8 flex justify-center">
                <Button variant="secondary" loading={loading} onClick={() => void runSearch(offset, true)}>
                  {tr("Carregar mais")}
                </Button>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}

function ResultCard({ item }: { item: SearchItem }): JSX.Element {
  const location = [item.district, item.city].filter(Boolean).join(' — ');
  return (
    <Link
      to={`/profissional/${item.professionalSlug}`}
      className="group block h-full rounded-lg border border-line bg-card p-4 transition-colors hover:border-graphite"
    >
      <div className="flex gap-3">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md bg-surface text-muted">
          {item.photoPath ? (
            <img src={item.photoPath} alt="" className="h-full w-full object-cover" />
          ) : (
            <Icon name="user" size={26} />
          )}
        </span>
        <div className="min-w-0">
          <p className="truncate font-semibold text-ink">{item.professionalName}</p>
          {item.title ? <p className="truncate text-sm text-muted">{item.title}</p> : null}
          <p className="truncate text-sm text-muted">{item.businessName}</p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2 text-xs">
        {item.categoryLabel ? (
          <span className="rounded-full bg-surface px-2.5 py-1 text-graphite">{item.categoryLabel}</span>
        ) : null}
        <span className="rounded-full bg-surface px-2.5 py-1 text-graphite">
          {item.serviceCount} {item.serviceCount === 1 ? tr('serviço') : tr('serviços')}
        </span>
      </div>

      <dl className="mt-3 space-y-1 text-sm">
        {location ? (
          <div className="flex items-center gap-2 text-muted">
            <Icon name="calendar" size={15} />
            <dd>{location}</dd>
          </div>
        ) : null}
        <div className="flex items-center justify-between text-muted">
          <dt>{tr("A partir de")}</dt>
          <dd className="font-semibold text-ink">{item.minPriceCents === null ? 'Preço sob consulta' : brl(item.minPriceCents)}</dd>
        </div>
      </dl>
    </Link>
  );
}
